import React, { useLayoutEffect, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ButtonIconOnly from "components/ButtonIconOnly";

const Popout = ({ 
  isOpen, 
  onClose, 
  anchorRef, 
  positioning = "bottom",
  stickPosition = false,
  header = true,
  title = "Popout Title", 
  children 
}) => {
  const popoutRef = useRef(null);
  const [position, setPosition] = useState(null); // Start as null to prevent flicker
  const [maxHeight, setMaxHeight] = useState(null);

  useLayoutEffect(() => {
    if (isOpen && anchorRef?.current && popoutRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const popoutWidth = popoutRef.current?.offsetWidth || 0;
      const popoutHeight = popoutRef.current?.offsetHeight || 0;
      const spacing = 8; // Space between the button and popout
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = 8; // Margin from viewport edges

      let top, left, calculatedMaxHeight = null;

      switch (positioning) {
        case "top":
          top = anchorRect.top - popoutHeight - spacing + window.scrollY;
          left = anchorRect.left + (anchorRect.width / 2) - (popoutWidth / 2);
          // Calculate available space above
          if (stickPosition) {
            calculatedMaxHeight = anchorRect.top - spacing - margin;
          }
          break;
        case "top-left":
          top = anchorRect.top - popoutHeight - spacing + window.scrollY;
          left = anchorRect.right - popoutWidth;
          if (stickPosition) {
            calculatedMaxHeight = anchorRect.top - spacing - margin;
          }
          break;
        case "top-right":
          top = anchorRect.top - popoutHeight - spacing + window.scrollY;
          left = anchorRect.right - popoutWidth;
          if (stickPosition) {
            calculatedMaxHeight = anchorRect.top - spacing - margin;
          }
          break;
        case "bottom":
          top = anchorRect.bottom + spacing + window.scrollY;
          left = anchorRect.left + (anchorRect.width / 2) - (popoutWidth / 2);
          // Calculate available space below
          if (stickPosition) {
            calculatedMaxHeight = viewportHeight - anchorRect.bottom - spacing - margin;
          }
          break;
        case "bottom-left":
          top = anchorRect.bottom + spacing + window.scrollY;
          left = anchorRect.right - popoutWidth;
          if (stickPosition) {
            calculatedMaxHeight = viewportHeight - anchorRect.bottom - spacing - margin;
          }
          break;
        case "bottom-right":
          top = anchorRect.bottom + spacing + window.scrollY;
          left = anchorRect.right - popoutWidth;
          if (stickPosition) {
            calculatedMaxHeight = viewportHeight - anchorRect.bottom - spacing - margin;
          }
          break;
        case "left":
          top = anchorRect.top + (anchorRect.height / 2) - (popoutHeight / 2) + window.scrollY;
          left = anchorRect.left - popoutWidth - spacing;
          // For left/right, limit height to viewport
          if (stickPosition) {
            calculatedMaxHeight = viewportHeight - margin * 2;
          }
          break;
        case "right":
          top = anchorRect.top + (anchorRect.height / 2) - (popoutHeight / 2) + window.scrollY;
          left = anchorRect.right + spacing;
          if (stickPosition) {
            calculatedMaxHeight = viewportHeight - margin * 2;
          }
          break;
        default:
          top = anchorRect.bottom + spacing + window.scrollY;
          left = anchorRect.left + (anchorRect.width / 2) - (popoutWidth / 2);
          if (stickPosition) {
            calculatedMaxHeight = viewportHeight - anchorRect.bottom - spacing - margin;
          }
      }

      // Only clamp horizontal position to prevent horizontal overflow
      if (left + popoutWidth > viewportWidth) {
        left = viewportWidth - popoutWidth - margin;
      }
      if (left < 0) {
        left = margin;
      }

      // For vertical positioning, only prevent popout from going off-screen
      // but don't reposition it on top of the anchor
      if (stickPosition && positioning.includes("top")) {
        // If positioned above anchor, ensure it doesn't go above viewport
        if (top < margin) {
          top = margin;
        }
      } else if (stickPosition && positioning.includes("bottom")) {
        // If positioned below anchor, keep it below
        // Height constraint will handle viewport overflow
      } else {
        // For left/right positioning, center vertically but clamp to viewport
        if (top < margin) {
          top = margin;
        }
        if (top + popoutHeight > viewportHeight - margin) {
          top = viewportHeight - popoutHeight - margin;
        }
      }

      setPosition({ top, left });
      setMaxHeight(calculatedMaxHeight);
    };
  }, [isOpen, anchorRef, positioning, children]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInAnyPopout = event.target.closest(".popout-root");
      if (!isInAnyPopout && !anchorRef?.current.contains(event.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div 
      ref={popoutRef} 
      className="popout-root absolute z-[1050] bg-[var(--color-white)] shadow-lg rounded-lg flex flex-col"
      style={{
        position: "absolute",
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? "visible" : "hidden",
        maxHeight: maxHeight ? `${maxHeight}px` : "none",
      }}
    >
      {/* Popout Header */}
      {header && <div className="flex justify-between items-center p-6 shrink-0">
        <h4 className="tw-heading-4">{title}</h4>
        <ButtonIconOnly
          id={`${title}-modal-close-button`}
          data-cy={`${title}-modal-close-button`}
          icon="close"
          size={20}
          type="alt"
          onClick={onClose}
        />
      </div>}

      {/* Dynamic Content */}
      <div className="pb-6 px-6 overflow-y-auto custom-scrollbar">
        {children}
      </div>
    </div>,
    document.body
  );
};

export default Popout;
